/**
 * SignatureFormateurPublic.jsx
 * Page publique (smartphone) — le formateur enregistre sa signature, utilisée sur les attestations de formation.
 * Accessible via /signature-formateur/:token (lien + QR code par e-mail ; code à 6 chiffres par SMS).
 * Parcours : code SMS → texte d'autorisation + consigne de qualité → tracé → aperçu → validation.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SignatureCanvas } from '../components/Shared';

const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

const page = { minHeight: '100vh', background: '#f1f5f9', padding: '16px 12px', fontFamily: 'Arial, Helvetica, sans-serif', color: '#1e293b', boxSizing: 'border-box' };
const carte = { maxWidth: 620, margin: '0 auto', background: '#fff', borderRadius: 14, padding: '20px 18px', boxShadow: '0 4px 20px rgba(0,0,0,.08)' };
const bouton = (fond = '#1a3668') => ({ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: fond, color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' });

async function appeler(chemin, corps) {
  const r = await fetch(`${API}/signature-formateur-public/${chemin}`, corps
    ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) }
    : undefined);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || 'Erreur'), { status: r.status });
  return data;
}

function Enveloppe({ children }) {
  return (
    <div style={page}>
      <div style={carte}>
        <div style={{ textAlign: 'center', borderBottom: '3px solid #E87722', paddingBottom: 10, marginBottom: 16, fontWeight: 700, fontSize: 18, color: '#E87722' }}>CEJ Concept</div>
        {children}
      </div>
    </div>
  );
}

export default function SignatureFormateurPublic() {
  const { token } = useParams();
  const [etape, setEtape] = useState('chargement'); // chargement | erreur | ordinateur | code | signature | apercu | fini
  const [info, setInfo] = useState(null);           // { nom, qr }
  const [erreur, setErreur] = useState('');
  const [code, setCode] = useState('');
  const [texte, setTexte] = useState(null);
  const [consentement, setConsentement] = useState(false);
  const [image, setImage] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [copie, setCopie] = useState(false);
  const [portrait, setPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const maj = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', maj);
    return () => window.removeEventListener('resize', maj);
  }, []);

  useEffect(() => {
    appeler(token)
      .then(d => {
        setInfo(d);
        // Ouvert depuis un ordinateur (sans écran tactile) : la signature se fait au smartphone
        setEtape((navigator.maxTouchPoints || 0) > 0 ? 'code' : 'ordinateur');
      })
      .catch(e => { setErreur(e.message); setEtape('erreur'); });
  }, [token]);

  async function validerCode(ev) {
    ev.preventDefault();
    setEnvoi(true); setErreur('');
    try {
      const d = await appeler(`${token}/acces`, { code });
      setTexte(d.texte);
      setEtape('signature');
    } catch (e) {
      setErreur(e.message);
      if (e.status === 429 || e.status === 410 || e.status === 404) setEtape('erreur');
    } finally { setEnvoi(false); }
  }

  async function enregistrer() {
    setEnvoi(true); setErreur('');
    try {
      const d = await appeler(`${token}/signer`, { code, consentement: true, image });
      setCopie(!!d.copie_envoyee);
      setEtape('fini');
    } catch (e) {
      setErreur(e.message);
      setEtape(e.status === 422 ? 'signature' : e.status === 429 || e.status === 410 || e.status === 404 ? 'erreur' : 'apercu');
    } finally { setEnvoi(false); }
  }

  if (etape === 'chargement') return <Enveloppe><p style={{ textAlign: 'center', color: '#64748b' }}>⏳ Chargement…</p></Enveloppe>;

  if (etape === 'erreur') return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>🔒</div>
        <h2 style={{ margin: '8px 0' }}>Lien non disponible</h2>
        <p style={{ color: '#475569', fontSize: 15 }}>{erreur}</p>
      </div>
    </Enveloppe>
  );

  if (etape === 'ordinateur') return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>📱</div>
        <h2 style={{ margin: '8px 0' }}>Ouvrez ce lien sur votre smartphone</h2>
        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5 }}>
          Votre signature doit être tracée avec le doigt sur l'écran d'un smartphone, pour être de qualité.
          Scannez ce QR code avec l'appareil photo de votre téléphone.
        </p>
        {info?.qr && <img src={info.qr} alt="QR code de la page de signature" width="220" height="220" style={{ border: '3px solid #e2e8f0', borderRadius: 10 }} />}
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 12 }}>Le code à 6 chiffres reçu par SMS vous sera demandé sur le téléphone.</p>
      </div>
    </Enveloppe>
  );

  if (etape === 'code') return (
    <Enveloppe>
      <h2 style={{ margin: '0 0 6px' }}>Bonjour {info?.nom}</h2>
      <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5, marginTop: 0 }}>
        Pour enregistrer votre signature, saisissez le <strong>code à 6 chiffres</strong> que vous avez reçu <strong>par SMS</strong>.
      </p>
      <form onSubmit={validerCode}>
        <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric" autoComplete="one-time-code" placeholder="000000" aria-label="Code reçu par SMS" autoFocus
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 28, letterSpacing: 8, textAlign: 'center', padding: '12px', borderRadius: 10, border: '2px solid #1a3668', marginBottom: 12 }} />
        {erreur && <div role="alert" style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{erreur}</div>}
        <button type="submit" disabled={code.length !== 6 || envoi} style={{ ...bouton(), opacity: code.length === 6 && !envoi ? 1 : 0.5 }}>
          {envoi ? '⏳ Vérification…' : 'Continuer'}
        </button>
      </form>
    </Enveloppe>
  );

  if (etape === 'signature' && texte) return (
    <Enveloppe>
      <h2 style={{ fontSize: 16, textAlign: 'center', margin: '0 0 12px' }}>{texte.titre}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: '#334155' }}>
        <p style={{ marginTop: 0 }}>{texte.intro}</p>
        {texte.articles.map(a => (
          <p key={a.titre} style={{ margin: '0 0 8px' }}><strong>{a.titre}.</strong> {a.texte}</p>
        ))}
      </div>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '14px 0', padding: 12, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
        <input type="checkbox" checked={consentement} onChange={e => setConsentement(e.target.checked)} style={{ width: 22, height: 22, flexShrink: 0, marginTop: 1 }} />
        <span>{texte.case}</span>
      </label>

      <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: 12, margin: '0 0 12px', fontSize: 14, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 4 }}>⚠ {texte.consigne[0]}</div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {texte.consigne.slice(1).map(c => <li key={c}>{c}</li>)}
        </ul>
      </div>
      {portrait && (
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: 10, marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
          📱 Tournez votre téléphone en travers (mode paysage) pour disposer d'un cadre plus large.
        </div>
      )}
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{erreur}</div>}
      <div style={{ opacity: consentement ? 1 : 0.4, pointerEvents: consentement ? 'auto' : 'none' }}>
        <SignatureCanvas controleQualite hauteur={portrait ? 200 : 170} labelValider="Voir l'aperçu"
          onSave={img => { setImage(img); setErreur(''); setEtape('apercu'); }} />
      </div>
      {!consentement && <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center' }}>Cochez la case ci-dessus pour pouvoir signer.</p>}
    </Enveloppe>
  );

  if (etape === 'apercu') return (
    <Enveloppe>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Votre signature</h2>
      <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>Voici le rendu sur l'attestation, à sa taille réelle. Elle vous convient ?</p>
      <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 14 }}>
        <img src={image} alt="Aperçu de votre signature" style={{ maxWidth: 180, maxHeight: 76, objectFit: 'contain' }} />
      </div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Agrandie</div>
        <img src={image} alt="" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
      </div>
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{erreur}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={() => { setImage(null); setErreur(''); setEtape('signature'); }} style={{ ...bouton('#fff'), color: '#475569', border: '1px solid #cbd5e1', flex: 1 }}>Recommencer</button>
        <button type="button" onClick={enregistrer} disabled={envoi} style={{ ...bouton('#16a34a'), flex: 2, opacity: envoi ? 0.6 : 1 }}>{envoi ? '⏳ Enregistrement…' : '✓ Valider ma signature'}</button>
      </div>
    </Enveloppe>
  );

  return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <h2 style={{ margin: '8px 0' }}>Signature enregistrée</h2>
        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5 }}>
          Merci {info?.nom}. Votre signature sera apposée sur les attestations de formation.
          {copie && ' Une copie de votre autorisation vous est envoyée par e-mail.'}
        </p>
        <p style={{ color: '#64748b', fontSize: 13 }}>Vous pouvez fermer cette page.</p>
      </div>
    </Enveloppe>
  );
}
